import { cn } from "@/modules/lib/utils"
import { word } from "@modules/core/utils/i18n"
import { ProfileAvatar } from "@modules/profile/common/components/profileAvatar"
import { ProgramBundlePriceCard } from "@modules/programBundle/components/programBundlePriceCard"
import { ProgramBundleEntity } from "@modules/programBundle/entities/programBundleEntity"
import { useProgramBundleRights } from "@modules/programBundle/hooks/useProgramBundleRights"
import { useProgramBundleSubscription } from "@modules/programBundle/hooks/useProgramBundleSubscription"
import { usePrograms } from "@modules/programs/hooks/usePrograms"
import { useProgramSidebar } from "@modules/programs/hooks/useProgramSidebar"
import { ProgramFilter } from "@modules/programs/types/programTypes"
import { HURichTextView } from "@modules/ui/components/richText/huRichTextView"
import { formatTextToRichText, isRichTextLongerThan } from "@modules/ui/components/richText/richTextUtils"
import { useMemo } from "react"
import { Text } from "@/components/ui/baseUi/text"

type ProgramBundleInfoDetailsProps = {
  programBundle: ProgramBundleEntity
  className?: string
} & React.ComponentProps<"div">

export function ProgramBundleInfoDetails({ programBundle, className, ...props }: ProgramBundleInfoDetailsProps) {
  const { isAuthor } = useProgramBundleRights(programBundle.id)
  const { navigateToProgramView } = useProgramSidebar()
  const { data: programBundleStripeSubscriptionInfos } = useProgramBundleSubscription(programBundle.id)
  const subscribedProgramsAsync = usePrograms({ programFilter: ProgramFilter.SUBSCRIBED })
  const programSubscribedInBundle = subscribedProgramsAsync.data.content.filter(
    (program) => programBundle.programs?.findIndex((p) => p.id === program.id) !== -1,
  )

  const onProgramClick = (programId: string) => {
    navigateToProgramView(programId, false, {
      leftButtonColor: "var(--textOnColor-body)",
      replaceAllComponents: false,
    })
  }

  const richTextDescription = useMemo(
    () => formatTextToRichText(programBundle.description, true),
    [programBundle.description],
  )

  return (
    <div className={cn("flex w-full flex-col gap-4 px-5 pt-16 pb-5", className)} {...props}>
      {/* Description */}
      <Text
        fontStyle="BM"
        className="line-clamp-4 wrap-break-word whitespace-break-spaces"
        readMore={isRichTextLongerThan({ richText: richTextDescription, numberOfLines: 4, maxLineLength: 70 })}
      >
        <HURichTextView value={richTextDescription} paragrapheFontStyle="BM" />
      </Text>

      {/* Information about already subscribed in a program of the bundle */}
      {!programBundleStripeSubscriptionInfos && !isAuthor && programSubscribedInBundle.length > 0 && (
        <div className="card banner">
          <Text fontStyle="BM" className="text-textDefault-body">
            {word("program.subscription.doubleSubscription.fromBundle", { count: programSubscribedInBundle.length }) +
              " :"}
          </Text>

          <div className="mt-2 ml-2 flex flex-col gap-2">
            {programSubscribedInBundle.map((program) => (
              <div
                className="flex cursor-pointer flex-row items-center gap-2"
                key={program.id}
                onClick={() => onProgramClick(program.id)}
              >
                <ProfileAvatar profile={{ avatar: program.image, username: "" }} className="size-18" />
                <Text fontStyle="BM" className="text-textDefault-body">
                  {program.name}
                </Text>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Price */}
      <ProgramBundlePriceCard programBundle={programBundle} />

      {/* Owner */}
      <div className="mb-4 flex w-full flex-row items-center justify-center gap-4 overflow-hidden rounded-4xl border-0 bg-surface-foreground p-4">
        <ProfileAvatar
          profile={{ avatar: programBundle.owner.avatar, username: programBundle.owner.firstname || "" }}
          className="size-24"
        />

        <div className="flex flex-col gap-1 overflow-hidden text-center">
          <Text color="currentColor" fontStyle="HS" className="overflow-hidden text-ellipsis text-textDefault-heading">
            {programBundle.owner.firstname + " " + programBundle.owner.lastname}
          </Text>

          {programBundle.owner.username && (
            <Text
              fontStyle="BM"
              color="currentColor"
              className="overflow-hidden text-ellipsis text-textDefault-heading"
            >
              {"@" + programBundle.owner.username}
            </Text>
          )}
        </div>
      </div>
    </div>
  )
}
